import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Mic, Square, Upload, Loader2, FileAudio, X, Clock } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { API } from "../App";

const POLL_INTERVAL = 3000;

const AudioImporter = ({ sessionType, onFieldsGenerated, patientId, autoStart = false }) => {
  const { t } = useI18n();
  const [mode, setMode] = useState(null); // null | "recording" | "recorded" | "uploaded"
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [transcription, setTranscription] = useState("");
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);
  useEffect(() => () => { // démontage : couper le micro pour ne pas laisser un enregistrement orphelin en arrière-plan
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") { rec.onstop = null; rec.stop(); }
    rec?.stream?.getTracks().forEach((t) => t.stop());
  }, []);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setMode("recorded");
        stream.getTracks().forEach((t) => t.stop());
      };

      recorder.start(1000);
      setIsRecording(true);
      setMode("recording");
      setDuration(0);
      timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
    } catch (err) {
      toast.error("Impossible d'acceder au microphone. Verifiez les permissions.");
    }
  };

  const autoStarted = useRef(false);
  useEffect(() => {
    if (autoStart && !autoStarted.current) {
      autoStarted.current = true;
      startRecording();
    }
  }, [autoStart]); // eslint-disable-line react-hooks/exhaustive-deps

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    const allowed = ["mp3", "mp4", "wav", "webm", "m4a", "ogg", "mpeg", "mpga"];
    if (!allowed.includes(ext)) {
      toast.error("Format non supporte. Formats acceptes : " + allowed.join(", "));
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      toast.error("Le fichier depasse 200 Mo");
      return;
    }
    setAudioBlob(file);
    setAudioUrl(URL.createObjectURL(file));
    setMode("uploaded");
  };

  const reset = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setMode(null);
    setAudioBlob(null);
    setAudioUrl(null);
    setTranscription("");
    setProcessing(false);
    setProgress(0);
    setDuration(0);
  };

  const processAudio = async () => {
    if (!audioBlob) return;
    setProcessing(true);
    setProgress(10);

    try {
      const formData = new FormData();
      const filename = audioBlob.name || "recording.webm";
      formData.append("audio", audioBlob, filename);
      formData.append("session_type", sessionType);
      if (patientId) formData.append("patient_id", patientId);

      setProgress(30);

      // Step 1: Transcribe + generate
      const res = await axios.post(`${API}/session-reports/generate-from-audio`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });

      setTranscription(res.data.transcription);
      setProgress(60);

      // Step 2: Poll for AI generation result
      const genId = res.data.generation_id;
      let attempts = 0;
      const maxAttempts = 40;

      const poll = async () => {
        while (attempts < maxAttempts) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL));
          attempts++;
          setProgress(60 + Math.min(35, attempts * 2));

          try {
            const statusRes = await axios.get(`${API}/session-reports/generation/${genId}`, { withCredentials: true });
            if (statusRes.data.status === "completed") {
              onFieldsGenerated(statusRes.data.fields || {}, statusRes.data.suggested_exercises || [], statusRes.data.transcription || transcription);
              toast.success(`Compte rendu généré à partir de l'enregistrement${statusRes.data.route?.label ? ` · ${statusRes.data.route.label}` : ""}`);
              setProgress(100);
              return;
            } else if (statusRes.data.status === "error") {
              throw new Error(statusRes.data.error || "Erreur de generation");
            }
          } catch (e) {
            if (e.message?.includes("Erreur")) throw e;
          }
        }
        throw new Error("Timeout de generation");
      };

      await poll();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Erreur";
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Card data-testid="audio-importer">
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-4">
          <FileAudio className="w-5 h-5 text-primary" />
          <h3 className="font-medium">Enregistrement audio de la seance</h3>
        </div>

        {!mode && (
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="outline" className="flex-1 h-24 flex-col gap-2" onClick={startRecording} data-testid="start-recording-btn">
              <Mic className="w-6 h-6 text-red-500" />
              <span className="text-sm">Enregistrer la seance</span>
            </Button>
            <Button variant="outline" className="flex-1 h-24 flex-col gap-2" onClick={() => fileInputRef.current?.click()} data-testid="upload-audio-btn">
              <Upload className="w-6 h-6 text-primary" />
              <span className="text-sm">Importer un fichier audio</span>
            </Button>
            <input ref={fileInputRef} type="file" accept="audio/*,.mp3,.wav,.webm,.m4a,.ogg,.mp4" className="hidden" onChange={handleFileUpload} />
          </div>
        )}

        {mode === "recording" && (
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-950/50 flex items-center justify-center animate-pulse">
                <Mic className="w-8 h-8 text-red-500" />
              </div>
            </div>
            <div className="flex items-center gap-2 text-lg font-mono">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span data-testid="recording-timer">{formatTime(duration)}</span>
            </div>
            <p className="text-sm text-muted-foreground">Enregistrement en cours...</p>
            <Button variant="destructive" onClick={stopRecording} data-testid="stop-recording-btn">
              <Square className="w-4 h-4 mr-2" /> Arreter l'enregistrement
            </Button>
          </div>
        )}

        {(mode === "recorded" || mode === "uploaded") && audioUrl && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <FileAudio className="w-5 h-5 text-primary shrink-0" />
              <audio controls src={audioUrl} className="flex-1 h-10" />
              <Button variant="ghost" size="sm" onClick={reset} disabled={processing}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {transcription && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">Transcription :</p>
                <p className="text-sm text-blue-900 dark:text-blue-100 max-h-32 overflow-y-auto">{transcription.substring(0, 500)}{transcription.length > 500 ? "..." : ""}</p>
              </div>
            )}

            {processing && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {progress < 50 ? "Transcription en cours..." : "Generation du compte rendu..."}
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}

            {!processing && !transcription && (
              <Button onClick={processAudio} className="w-full" data-testid="process-audio-btn">
                <Loader2 className="w-4 h-4 mr-2" /> Transcrire et generer le compte rendu
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AudioImporter;
